import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import { Agent, Campaign, FetchedAgent, FetchedCampaign } from '../../utils/interfaces';
import axios from 'axios';
import { AMAZON_CAMPAIGN_ENDPOINTS2 } from '../../utils/constant';

interface CampaignDisplayProps {
  setAgents: (agents: FetchedAgent[]) => void;
  agents: FetchedAgent[],
  fetchAgents: (campaignId: string) => Promise<void>;
  campaigns: FetchedCampaign[];
  setCampaigns: React.Dispatch<React.SetStateAction<FetchedCampaign[]>>;
  selectedCampaign: FetchedCampaign;
  setSelectedCampaign: React.Dispatch<React.SetStateAction<FetchedCampaign>>;
}

const CampaignDisplay: React.FC<CampaignDisplayProps> = ({ setAgents, agents, fetchAgents, campaigns, setCampaigns, selectedCampaign, setSelectedCampaign }) => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openLoadDialog, setOpenLoadDialog] = useState(false);
  const [openAddAgentDialog, setOpenAddAgentDialog] = useState(false);

  const [newCampaign, setNewCampaign] = useState<Campaign>({
    nameOfTheCampaign: 'test',
    reasonForCampaignExistence: '',
    campaignStrengths: '',
    campaignWeaknesses: '',
    campaignOpportunities: '',
    campaignThreats: '',
    campaignGuidelines: '',
    targetAudience: '',
    campaignCreatorId: '',
    campaignID: '',
  });

  const [newAgent, setNewAgent] = useState<Agent>({
    agentName: '',
    agentDescription: '',
    agentDomain: '',
    platformName: '',
    campaignObjective: '',
    model: '',
  });

  const handleOpenAddAgentDialog = () => setOpenAddAgentDialog(true);
  const handleCloseAddAgentDialog = () => setOpenAddAgentDialog(false);

  const handleAddAgent = async () => {
    try {
      const response = await axios.post(`${AMAZON_CAMPAIGN_ENDPOINTS2}/agents/${selectedCampaign.campaign_id}`, newAgent);
      setAgents([...agents, response.data]);
      setNewAgent({
        agentName: '',
        agentDescription: '',
        agentDomain: '',
        platformName: '',
        campaignObjective: '',
        model: '',
      });
      handleCloseAddAgentDialog();
    } catch (error) {
      console.error('Error adding agent:', error);
    }
  };

  const handleAgentChange = (prop: keyof Agent) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewAgent({ ...newAgent, [prop]: event.target.value });
  };

  const handleOpenAddDialog = () => setOpenAddDialog(true);
  const handleCloseAddDialog = () => setOpenAddDialog(false);

  const handleOpenLoadDialog = () => setOpenLoadDialog(true);
  const handleCloseLoadDialog = () => setOpenLoadDialog(false);

  const handleLoadCampaign = (campaign: FetchedCampaign) => {
    setSelectedCampaign(campaign);
    fetchAgents(campaign.campaign_id ?? "")

    handleCloseLoadDialog();
  };

  const handleAddCampaign = async () => {
    setCampaigns([...campaigns, {name: newCampaign.nameOfTheCampaign, campaign_id: newCampaign.campaignID}]);
    const response = await axios.post(`${AMAZON_CAMPAIGN_ENDPOINTS2}/campaigns`, { name: newCampaign });
    console.log(response.data)
    handleCloseAddDialog();
  };


  const handleChange = (prop: keyof Campaign) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewCampaign({ ...newCampaign, [prop]: event.target.value });
    fetchAgents(newCampaign.campaign_id ?? "")

    console.log(newCampaign)
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Current Campaign: {selectedCampaign.name || 'None selected'}
      </Typography>
      <Button variant="outlined" sx={{ mr: 1 }} onClick={handleOpenLoadDialog}>
        Load Campaigns
      </Button>
      <Button variant="contained" color="primary" onClick={handleAddCampaign}>
        Add Campaign
      </Button>

      <Dialog open={openLoadDialog} onClose={handleCloseLoadDialog}>
        <DialogTitle>Load a Campaign</DialogTitle>
        <DialogContent>
          <List>
            {campaigns.map((campaign, index) => (
              <ListItem button key={index} onClick={() => handleLoadCampaign(campaign)}>
                <ListItemText primary={campaign.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLoadDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle>Add a New Campaign</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Campaign Name"
            fullWidth
            variant="standard"
            value={newCampaign.nameOfTheCampaign}
            onChange={handleChange('nameOfTheCampaign')}
          />
          <TextField
            margin="dense"
            id="reason"
            label="Reason for Campaign"
            fullWidth
            variant="standard"
            value={newCampaign.reasonForCampaignExistence}
            onChange={handleChange('reasonForCampaignExistence')}
          />
          <TextField
            margin="dense"
            id="strengths"
            label="Campaign Strengths"
            fullWidth
            variant="standard"
            value={newCampaign.campaignStrengths}
            onChange={handleChange('campaignStrengths')}
          />
          <TextField
            margin="dense"
            id="weaknesses"
            label="Campaign Weaknesses"
            fullWidth
            variant="standard"
            value={newCampaign.campaignWeaknesses}
            onChange={handleChange('campaignWeaknesses')}
          />
          <TextField
            margin="dense"
            id="opportunities"
            label="Campaign Opportunities"
            fullWidth
            variant="standard"
            value={newCampaign.campaignOpportunities}
            onChange={handleChange('campaignOpportunities')}
          />
          <TextField
            margin="dense"
            id="threats"
            label="Campaign Threats"
            fullWidth
            variant="standard"
            value={newCampaign.campaignThreats}
            onChange={handleChange('campaignThreats')}
          />
          <TextField
            margin="dense"
            id="guidelines"
            label="Campaign Guidelines"
            fullWidth
            variant="standard"
            value={newCampaign.campaignGuidelines}
            onChange={handleChange('campaignGuidelines')}
          />
          <TextField
            margin="dense"
            id="audience"
            label="Target Audience"
            fullWidth
            variant="standard"
            value={newCampaign.targetAudience}
            onChange={handleChange('targetAudience')}
          />
          <TextField
            margin="dense"
            id="creatorId"
            label="Campaign Creator ID"
            fullWidth
            variant="standard"
            value={newCampaign.campaignCreatorId}
            onChange={handleChange('campaignCreatorId')}
          />
          <TextField
            margin="dense"
            id="campaignID"
            label="Campaign ID"
            fullWidth
            variant="standard"
            value={newCampaign.campaignID}
            onChange={handleChange('campaignID')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddCampaign}>Add</Button>
        </DialogActions>
      </Dialog>

      <Box mt={2}>
        <Typography variant="h6">Agents:</Typography>
        <List>
          {agents.map((agent, index) => (
            <ListItem key={index}>
              <ListItemText primary={agent.name} />
            </ListItem>
          ))}
        </List>
      </Box>
      {selectedCampaign.campaign_id && (
        <Button variant="contained" color="primary" onClick={handleOpenAddAgentDialog}>
          Add Agent
        </Button>
      )}

<Dialog open={openAddAgentDialog} onClose={handleCloseAddAgentDialog}>
  <DialogTitle>Add a New Agent</DialogTitle>
  <DialogContent>
    <TextField
      autoFocus
      margin="dense"
      id="agentName"
      label="Agent Name"
      fullWidth
      variant="standard"
      value={newAgent.agentName}
      onChange={handleAgentChange('agentName')}
    />
    <TextField
      margin="dense"
      id="agentDescription"
      label="Agent Description"
      fullWidth
      variant="standard"
      value={newAgent.agentDescription}
      onChange={handleAgentChange('agentDescription')}
    />
    <TextField
      margin="dense"
      id="agentDomain"
      label="Agent Domain"
      fullWidth
      variant="standard"
      value={newAgent.agentDomain}
      onChange={handleAgentChange('agentDomain')}
    />
    <TextField
      margin="dense"
      id="platformName"
      label="Platform Name"
      fullWidth
      variant="standard"
      value={newAgent.platformName}
      onChange={handleAgentChange('platformName')}
    />
    <TextField
      margin="dense"
      id="campaignObjective"
      label="Campaign Objective"
      fullWidth
      variant="standard"
      value={newAgent.campaignObjective}
      onChange={handleAgentChange('campaignObjective')}
    />
    <TextField
      margin="dense"
      id="model"
      label="Model"
      fullWidth
      variant="standard"
      value={newAgent.model}
      onChange={handleAgentChange('model')}
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseAddAgentDialog}>Cancel</Button>
    <Button onClick={handleAddAgent}>Add</Button>
  </DialogActions>
</Dialog>

    </Box>
  );
};

export default CampaignDisplay;
